/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { SupportRequestStatus, SupportRequestType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface SupportRequestProps {
  id: string;
  subject: string | null;
  content: string | null;
  supportRequestType: SupportRequestType;
  isEmailSent: boolean;
  supportRequestStatus: SupportRequestStatus;
  notes: string | null;
  personId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class SupportRequestRecord extends Record<SupportRequestProps>({
  id: null as any,
  subject: null as any,
  content: null as any,
  supportRequestType: null as any,
  isEmailSent: null as any,
  supportRequestStatus: null as any,
  notes: null as any,
  personId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    subject: string | null;
    content: string | null;
    supportRequestType: SupportRequestType;
    isEmailSent: boolean;
    supportRequestStatus: SupportRequestStatus;
    notes: string | null;
    personId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): SupportRequestRecord {
    if (json == null) {
      throw new Error('SupportRequestRecord.fromJS: parameter cannot be null');
    }
    return new SupportRequestRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'SupportRequestJson.id',
      ),
      subject: json.subject,
      content: json.content,
      supportRequestType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'supportRequestType', SupportRequestType.Unspecified),
        'SupportRequestJson.supportRequestType',
      ),
      isEmailSent: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isEmailSent', false),
        'SupportRequestJson.isEmailSent',
      ),
      supportRequestStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'supportRequestStatus', SupportRequestStatus.Created),
        'SupportRequestJson.supportRequestStatus',
      ),
      notes: json.notes,
      personId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
        'SupportRequestJson.personId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'SupportRequestJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    subject: string | null;
    content: string | null;
    supportRequestType: SupportRequestType;
    isEmailSent: boolean;
    supportRequestStatus: SupportRequestStatus;
    notes: string | null;
    personId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [SupportRequestRecord, Error[]] {
    if (json == null) {
      throw new Error('SupportRequestRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new SupportRequestRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'SupportRequestJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      subject: tryOrDefault(
        () => json.subject,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      content: tryOrDefault(
        () => json.content,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      supportRequestType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'supportRequestType', SupportRequestType.Unspecified),
            'SupportRequestJson.supportRequestType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isEmailSent: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isEmailSent', false),
            'SupportRequestJson.isEmailSent',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      supportRequestStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'supportRequestStatus', SupportRequestStatus.Created),
            'SupportRequestJson.supportRequestStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      personId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
            'SupportRequestJson.personId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'SupportRequestJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: SupportRequestProps) {
    super(props);
    if (props == null) {
      throw new Error('SupportRequestRecord constructor parameter cannot be null');
    }
  }
}

export abstract class SupportRequestViewModelBase implements SupportRequestProps {
  protected readonly _props: SupportRequestProps;
  private _json?: SupportRequestProps;

  get id(): string {
    return this._props.id;
  }
  get subject(): string | null {
    return this._props.subject;
  }
  get content(): string | null {
    return this._props.content;
  }
  get supportRequestType(): SupportRequestType {
    return this._props.supportRequestType;
  }
  get isEmailSent(): boolean {
    return this._props.isEmailSent;
  }
  get supportRequestStatus(): SupportRequestStatus {
    return this._props.supportRequestStatus;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get personId(): string {
    return this._props.personId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isBug() {
    return this._props.supportRequestType === SupportRequestType.Bug;
  }
  public get isFeature() {
    return this._props.supportRequestType === SupportRequestType.Feature;
  }
  public get isUnspecified() {
    return this._props.supportRequestType === SupportRequestType.Unspecified;
  }
  public get isAccepted() {
    return this._props.supportRequestStatus === SupportRequestStatus.Accepted;
  }
  public get isCompleted() {
    return this._props.supportRequestStatus === SupportRequestStatus.Completed;
  }
  public get isCreated() {
    return this._props.supportRequestStatus === SupportRequestStatus.Created;
  }
  public get isInProgress() {
    return this._props.supportRequestStatus === SupportRequestStatus.InProgress;
  }
  public get isRejected() {
    return this._props.supportRequestStatus === SupportRequestStatus.Rejected;
  }
  public get isSubmitted() {
    return this._props.supportRequestStatus === SupportRequestStatus.Submitted;
  }

  public isAnySupportRequestType(...supportRequestType: SupportRequestType[]) {
    return supportRequestType.includes(this._props.supportRequestType);
  }
  public isAnySupportRequestStatus(...supportRequestStatus: SupportRequestStatus[]) {
    return supportRequestStatus.includes(this._props.supportRequestStatus);
  }

  constructor(props: SupportRequestProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        subject: this._props.subject,
        content: this._props.content,
        supportRequestType: this._props.supportRequestType,
        isEmailSent: this._props.isEmailSent,
        supportRequestStatus: this._props.supportRequestStatus,
        notes: this._props.notes,
        personId: this._props.personId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllSupportRequestEntities = (state: AppState) =>
  state.entityStores.supportRequest.entities;

export const selectSupportRequestById = (id: string) => (state: AppState) =>
  state.entityStores.supportRequest.entities.get(id);

export const selectSupportRequestMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.supportRequest.meta.get(id);

export const selectSupportRequestAndMetadataById = (id: string) =>
  createSelector(
    selectSupportRequestById(id),
    selectSupportRequestMetadataById(id),
    (supportRequest, meta) => ({ supportRequest, meta }),
  );

export function selectSupportRequestEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllSupportRequestEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, SupportRequestRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectSupportRequestEntitiesBySubject(subject: string) {
  return createSelector(selectAllSupportRequestEntities, (entities) =>
    entities.filter((e) => e.subject === subject).valueSeq(),
  );
}

export function selectSupportRequestEntitiesByContent(content: string) {
  return createSelector(selectAllSupportRequestEntities, (entities) =>
    entities.filter((e) => e.content === content).valueSeq(),
  );
}

export function selectSupportRequestEntitiesBySupportRequestType(
  supportRequestType: SupportRequestType,
) {
  return createSelector(selectAllSupportRequestEntities, (entities) =>
    entities.filter((e) => e.supportRequestType === supportRequestType).valueSeq(),
  );
}

export function selectSupportRequestEntitiesByIsEmailSent(isEmailSent: boolean) {
  return createSelector(selectAllSupportRequestEntities, (entities) =>
    entities.filter((e) => e.isEmailSent === isEmailSent).valueSeq(),
  );
}

export function selectSupportRequestEntitiesBySupportRequestStatus(
  supportRequestStatus: SupportRequestStatus,
) {
  return createSelector(selectAllSupportRequestEntities, (entities) =>
    entities.filter((e) => e.supportRequestStatus === supportRequestStatus).valueSeq(),
  );
}

export function selectSupportRequestEntitiesByNotes(notes: string) {
  return createSelector(selectAllSupportRequestEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectSupportRequestEntitiesByPersonId(personId: string) {
  return createSelector(selectAllSupportRequestEntities, (entities) =>
    entities.filter((e) => e.personId === personId).valueSeq(),
  );
}

export function selectSupportRequestEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllSupportRequestEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
